import { Component, inject, signal } from '@angular/core';
import { ActionButton } from '../../../../../shared/action-button/action-button';
import { LoadingIndicator } from '../../../../../shared/loading-indicator/loading-indicator';
import { NotFoundError } from '../../../../../shared/not-found-error/not-found-error';
import { Pagination } from '../../../../../shared/pagination/pagination';
import { FormsModule, NgForm, ReactiveFormsModule } from '@angular/forms';
import { Helpers } from '../../../../../utilities/helpers';
import { NgClass } from '@angular/common';
import { Session } from '../../../../../services/session';
import { SchoolService } from '../../../../../services/school';
import { FlashService } from '../../../../../services/flash';
import { ISchools } from '../../../../../interfaces/school';
import { ApiEndpoints } from '../../../../../core/api-endpoints';
import { AcademicTermService } from '../../../../../services/academic-term';
import { IAcademicTerm, IAcademicTerms } from '../../../../../interfaces/academic-term';
import { AcademicYearService } from '../../../../../services/academic-year';
import { IAcademicYear, IAcademicYears } from '../../../../../interfaces/academic-year';

@Component({
  selector: 'app-terms',
  imports: [
    ActionButton,
    LoadingIndicator,
    NotFoundError,
    Pagination,
    ReactiveFormsModule,
    FormsModule,
    NgClass,
  ],
  templateUrl: './terms.html',
  styleUrl: './terms.css',
})
export class Terms {
  constructor(
    protected academicTermService: AcademicTermService,
    private session: Session,
    private schoolService: SchoolService,
    protected academicYearService: AcademicYearService,
  ) {
    const token = this.session.getItem('token');

    this.loggedUserRole = this.session.decodeToken(token)?.role;
    this.schoolId = this.session.decodeToken(token)?.school_id;

    if (this.loggedUserRole === 'SUPER_ADMIN') {
      this.getSchools();
    }
    this.getAcademicYears();
    this.getAcademicTerms();
  }

  protected academicYears = signal<IAcademicYears | null>(null);
  protected academicTerms = signal<IAcademicTerms | null>(null);
  protected readonly flashService = inject(FlashService);
  protected loggedUserRole?: string;
  protected schoolId?: string;
  protected pagination = signal({ page: 0, size: 10 });
  protected readonly Helpers = Helpers;
  protected schools = signal<ISchools | null>(null);
  protected currentFilterType = '';
  protected currentFilterValue = '';
  protected search: string = '';
  protected filterSchoolId: string = '';
  protected status: string = '';
  protected newStatus: string = '';
  protected isFiltering = signal<boolean>(false);
  protected selectedAcademicTerm!: IAcademicTerm['payload'];
  protected startDate = '';
  protected endDate = '';
  protected settingCurrentTermId = signal<string>('');
  protected changeStatus = signal<boolean>(false);
  protected editAcademicTerm = signal<boolean>(false);
  protected isEditing = signal<boolean>(false);
  protected validStatuses = ['ACTIVE', 'UPCOMING', 'COMPLETED', 'CANCELLED'];
  protected academicYearId: string = '';
  protected academicYear = signal<IAcademicYear | null>(null)

  protected getAcademicTerms(
    filterType = this.currentFilterType,
    value = this.currentFilterValue,
  ): void {
    this.isFiltering.set(true);

    this.currentFilterType = filterType;
    this.currentFilterValue = value;

    let { page, size } = this.pagination();

    const params: Record<string, string | number> = {
      page,
      size,
    };

    if (filterType && value) {
      params[filterType] = value;
    }
    this.academicTermService
      .getAcademicTerms(`${ApiEndpoints.url}/academic-term`, params)
      .subscribe({
        next: (res) => {
          this.academicTerms.set(res);
          this.isFiltering.set(false);

          const academicTerms = res.payload.map((term) => ({
            term_name: term.term_name,
            academic_year_id: term.academic_year_id,
          }));

          this.session.setItem('academicTerms', academicTerms);
        },
        error: (err) => {
          this.flashService.failure(err?.error?.message ?? 'Could not load Academic Terms. Refresh the page.');
        },
      });
  }

  protected getAcademicYears(): void {
    const params: Record<string, string | number> = {
      page: 0,
      size: 30,
    };

    this.academicYearService
      .getAcademicYears(`${ApiEndpoints.url}/academic-year`, params)
      .subscribe({
        next: (res) => {
          this.academicYears.set(res);
          this.isFiltering.set(false);
        },
        error: (err) => {
          this.flashService.failure(err?.error?.message ?? 'Could not load Academic Years. Refresh the page.');
        },
      });
  }

  protected getSchools(): void {
    this.schoolService.getSchools(`${ApiEndpoints.url}/school`).subscribe({
      next: (data) => {
        this.schools.set(data);
      },
      error: (err) => {
        this.flashService.failure(err?.error?.message ?? 'Error loading schools.');
      },
    });
  }

  protected loadPage(page: number): void {
    this.pagination.update((p) => ({
      ...p,
      page,
    }));

    this.getAcademicTerms();
  }

  protected getAcademicYear(id: string): void {
    this.academicYearService.getAcademicYearById(`${ApiEndpoints.url}/academic-year/${id}`).subscribe({
      next: (res) => {
        this.academicYear.set(res);
      },
      error: (err) => {
        this.flashService.failure(err?.error?.message ?? 'Could not load Academic Year. Refresh the page.');
      },
    });
  }

  protected selectAcademicTerm(action: string, payload: IAcademicTerm['payload']): void {
    if (action === 'status') {
      this.changeStatus.set(true);
    } else {
      this.editAcademicTerm.set(true);
      this.getAcademicYear(payload.academic_year_id)
    }

    this.selectedAcademicTerm = payload;

    this.startDate = new Date(this.selectedAcademicTerm.term_start_date).toISOString().split('T')[0];
    this.endDate = new Date(this.selectedAcademicTerm.term_end_date).toISOString().split('T')[0];
  }

  protected setCurrentAcademicTerm(payload: IAcademicTerm['payload']): void {
    this.selectedAcademicTerm = payload;
    this.settingCurrentTermId.set(payload.id);

    this.academicTermService
      .editAcademicTerm(`${ApiEndpoints.url}/academic-term/${payload.id}`, { current_term: true })
      .subscribe({
        next: () => {
          this.settingCurrentTermId.set('');
          this.flashService.success('Academic term set as current successfully');
          this.getAcademicTerms();
        },
        error: (err) => {
          this.settingCurrentTermId.set('');
          this.flashService.failure(err?.error?.message ?? 'Unable to set current academic term');
        },
      });
  }

  protected editAcademicTermFn(form: NgForm, action: 'status' | 'edit'): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    const payload: Record<string, any> = {};

    if (action === 'status') {
      if (this.newStatus !== this.selectedAcademicTerm.status) {
        payload['status'] = this.newStatus;
      }
    } else {
      if (this.startDate !== this.selectedAcademicTerm.term_start_date) {
        payload['start_date'] = this.startDate;
      }

      if (this.endDate !== this.selectedAcademicTerm.term_end_date) {
        payload['end_date'] = this.endDate;
      }
    }

    if (!Object.keys(payload).length) {
      this.flashService.failure('No changes detected');
      return;
    }

    this.isEditing.set(true);

    this.academicTermService
      .editAcademicTerm(
        `${ApiEndpoints.url}/academic-term/${this.selectedAcademicTerm.id}`,
        payload,
      )
      .subscribe({
        next: () => {
          this.isEditing.set(false);

          this.changeStatus.set(false);
          this.editAcademicTerm.set(false);

          this.flashService.success(
            action === 'status'
              ? 'Academic term status updated successfully'
              : 'Academic term updated successfully',
          );

          this.getAcademicTerms();
          this.newStatus = '';
        },

        error: (err) => {
          this.isEditing.set(false);

          this.flashService.failure(err?.error?.message ?? 'Unable to update academic year');
        },
      });
  }
}
